import React, { useEffect, useRef, useState } from "react";
import {
  Shield, TrendingUp, Landmark, Boxes, LineChart, ArrowRight,
} from "lucide-react";

export const ICONS = {
  shield: Shield,
  "trending-up": TrendingUp,
  landmark: Landmark,
  boxes: Boxes,
  "line-chart": LineChart,
};

export const SectionTag = ({ children }) => (
  <span className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#00E5FF]">
    <span className="h-px w-6 bg-[#00E5FF]/60" />
    {children}
  </span>
);

// Simple scroll reveal using IntersectionObserver + CSS
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

export const ArrowLink = ({ children }) => (
  <span className="inline-flex items-center gap-2 group text-[#1E90FF] font-medium text-sm">
    {children}
    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
  </span>
);

export const PageHero = ({ tag, title, subtitle, image }) => (
  <section className="relative pt-40 pb-20 overflow-hidden" data-testid="page-hero">
    <div className="absolute inset-0 grid-lines opacity-40" />
    {image && (
      <div className="absolute inset-0">
        <img src={image} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050506]/70 via-[#050506]/85 to-[#050506]" />
      </div>
    )}
    <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] bg-[#1E90FF]/10 blur-[120px] rounded-full" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12">
      <SectionTag>{tag}</SectionTag>
      <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white max-w-4xl leading-[1.05]">
        {title}
      </h1>
      <p className="mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">{subtitle}</p>
    </div>
  </section>
);
